import { useState } from 'react';
import { Button, Input, Modal } from '@react-apps/design-system';
import type { PerkPreset } from '../../domain/entities/catalog.entity';
import { useCreatePerkPreset } from '../hooks/use-perk-presets';

interface CreatePerkPresetModalProps {
  open: boolean;
  onClose: () => void;
  /** Chamado com o preset recém-criado, antes de fechar. */
  onCreated?: (preset: PerkPreset) => void;
  /** Sobrescreve a descrição padrão do modal (ex.: criar a partir de outro). */
  modalDescription?: string;
}

/**
 * O preset nasce vazio — chave, nome e descrição só. Os perks entram depois, um
 * a um, porque cada item precisa da forma de concessão e dos limites dela.
 */
export function CreatePerkPresetModal({
  open,
  onClose,
  onCreated,
  modalDescription,
}: CreatePerkPresetModalProps) {
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const { mutate, isPending, error, reset } = useCreatePerkPreset();

  const close = () => {
    reset();
    setKey('');
    setName('');
    setDescription('');
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutate(
      { key: key.trim(), name: name.trim(), description: description.trim() },
      {
        onSuccess: (preset) => {
          if (onCreated) {
            onCreated(preset);
          } else {
            close();
          }
        },
      },
    );
  };

  const canSubmit =
    key.trim().length > 0 &&
    name.trim().length > 0 &&
    description.trim().length > 0 &&
    !isPending;

  return (
    <Modal
      open={open}
      onClose={close}
      title="Criar preset"
      description={
        modalDescription ??
        'Um pacote nomeado de perks já configurados. Adicione os perks depois de criar.'
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Chave"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="adminDashboard.equipeConteudo"
          helperText="Como o back referencia este preset — formato {plataforma}.{nomeCamelCase}. Única."
          required
        />
        <Input
          label="Nome"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Equipe de conteúdo"
          required
        />
        <Input
          label="Descrição"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Acessos padrão de quem entra no time de conteúdo."
          required
        />
        {error && (
          <p className="text-sm text-red-600">
            {error instanceof Error ? error.message : 'Falha ao criar preset'}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={close}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {isPending ? 'Criando…' : 'Criar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
